import { supabase } from '../config/supabaseClient';

// Authentication Service - Handles auth operations with Supabase
class SupabaseAuthService {
	async login(email, password) {
		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email,
				password,
			});

			if (error) {
				throw error;
			}

			if (data.user && !data.user.email_confirmed_at) {
				await this.logout();
				return {
					user: null,
					session: null,
					error: 'Please confirm your email address before logging in. Check your inbox.',
				};
			}

			return {
				user: data.user,
				session: data.session,
				error: null,
			};
		} catch (error) {
			console.error('Login error:', error);

			let errorMessage = 'Login failed. Please try again.';

			if (error.message.includes('Invalid login credentials')) {
				errorMessage = 'Invalid email or password';
			} else if (error.message.includes('Email not confirmed')) {
				errorMessage = 'Please confirm your email address before logging in';
			}

			return {
				user: null,
				session: null,
				error: errorMessage,
			};
		}
	}

	async logout() {
		try {
			const { error } = await supabase.auth.signOut();

			if (error) {
				throw error;
			}

			return { error: null };
		} catch (error) {
			console.error('Logout error:', error);
			return {
				error: error.message || 'Logout failed',
			};
		}
	}

	async getSession() {
		try {
			const { data, error } = await supabase.auth.getSession();

			if (error) {
				throw error;
			}

			return {
				session: data.session,
				error: null,
			};
		} catch (error) {
			console.error('Get session error:', error);
			return {
				session: null,
				error: error.message,
			};
		}
	}

	async resetPassword(email) {
		try {
			const { error } = await supabase.auth.resetPasswordForEmail(email, {
				redirectTo: `${window.location.origin}/reset-password`,
			});

			if (error) {
				throw error;
			}

			return { error: null };
		} catch (error) {
			console.error('Reset password error:', error);
			return {
				error: error.message || 'Failed to send reset password email',
			};
		}
	}

	async updatePassword(newPassword) {
		try {
			const { data, error } = await supabase.auth.updateUser({
				password: newPassword,
			});

			if (error) {
				throw error;
			}

			return {
				user: data.user,
				error: null,
			};
		} catch (error) {
			console.error('Update password error:', error);
			return {
				user: null,
				error: error.message || 'Failed to update password',
			};
		}
	}

	onAuthStateChange(callback) {
		const { data: { subscription } } = supabase.auth.onAuthStateChange(
			(event, session) => {
				callback(event, session);
			}
		);

		return subscription;
	}
}

export const authService = new SupabaseAuthService();